import React from 'react';
import { cn } from '@/lib/utils';

interface InlineLoaderProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  text?: string;
  variant?: 'spinner' | 'dots' | 'pulse';
}

/**
 * InlineLoader - For buttons, form elements, and small loading states
 * Non-destructive loading that preserves layout
 */
export function InlineLoader({ 
  size = 'sm', 
  className,
  text,
  variant = 'spinner'
}: InlineLoaderProps) {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8'
  };

  const textSizes = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  if (variant === 'dots') {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className="flex space-x-1">
          <div className="w-1 h-1 rounded-full bg-primary/60 animate-pulse" style={{ animationDelay: '0ms' }} />
          <div className="w-1 h-1 rounded-full bg-primary/60 animate-pulse" style={{ animationDelay: '150ms' }} />
          <div className="w-1 h-1 rounded-full bg-primary/60 animate-pulse" style={{ animationDelay: '300ms' }} />
        </div>
        {text && <span className={cn("text-muted-foreground", textSizes[size])}>{text}</span>}
      </div>
    );
  }

  if (variant === 'pulse') {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className="relative">
          {/* Outer pulse ring */}
          <div className={cn(
            "absolute inset-0 rounded-full bg-primary/20 animate-ping",
            sizeClasses[size]
          )} />
          {/* Inner solid circle */}
          <div className={cn(
            "relative rounded-full bg-primary/60 animate-pulse",
            sizeClasses[size]
          )} />
        </div>
        {text && <span className={cn("text-muted-foreground animate-pulse", textSizes[size])}>{text}</span>}
      </div>
    );
  }

  // Default 'spinner' variant
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className={cn(
        "rounded-full border-2 border-primary/40 border-t-transparent animate-spin",
        sizeClasses[size]
      )} />
      {text && <span className={cn("text-muted-foreground", textSizes[size])}>{text}</span>}
    </div>
  );
}